import { PayloadAction, createAction, createSlice } from "@reduxjs/toolkit";
import { applyColumnResize, ColumnResizePayload } from '../../utils/list/utils/columnResize';
import { Preferences } from "../../profile/objects/preferences";
import { EMPTY_PURCHASES_RECEIPT_LIST_CONFIGURATION, EMPTY_PURCHASES_RECEIPT_LIST_CRITERIA, EMPTY_PURCHASES_RECEIPT_PAID_LIST_CONFIGURATION, EMPTY_PURCHASES_RECEIPT_PAID_LIST_CRITERIA, EMPTY_PURCHASES_RECEIPT_PROVIDER_LIST_CONFIGURATION, EMPTY_PURCHASES_RECEIPT_PROVIDER_LIST_CRITERIA, EMPTY_PURCHASES_RECEIPT_UNPAID_LIST_CONFIGURATION, EMPTY_PURCHASES_RECEIPT_UNPAID_LIST_CRITERIA, EMPTY_PURCHASES_STATS, EMPTY_THIRD_PARTY_LIST_CONFIGURATION, EMPTY_THIRD_PARTY_LIST_CRITERIA } from "../constant/purchasesReceiptConstant";
import { PurchasesReceipt } from "../objects/purchasesReceipt";
import { PurchasesReceiptListCriteria } from "../objects/purchasesReceiptListCriteria";
import { PurchasesReceiptState } from "../objects/purchasesReceiptState";
import { setPurchasesReceiptForecastPreference } from "../utils/setPurchasesReceiptForecastPreference";
import { Organisation } from "../../organisation/objects/organisation";
import { CustomProvider } from "../../customProvider/objects/customProvider";
import { setPurchasesReceiptProviderPreference } from "../utils/setPurchasesReceiptProviderPreference";
import { setPurchasesReceiptUnpaidPreference } from "../utils/setPurchasesReceiptUnpaidPreference";
import { setPurchasesReceiptPaidPreference } from "../utils/setPurchasesReceiptPaidPreference";
import { Bill } from "../objects/bill";
import { Column } from "../objects/column";
import { cloneDeep } from "lodash";
import { Currency } from "../../currency/objects/currency";
import { PurchasesReceiptPaidListCriteria } from "../objects/purchasesReceiptPaidListCriteria";
import { PurchasesReceiptProviderListCriteria } from "../objects/purchasesReceiptProviderListCriteria";
import { BillPayment } from "../objects/billPayment";
import { PurchasesStats } from "../objects/purchasesStats";
import { setThirdPartyPreference } from "../utils/setThirdPartyPreference";
import { ThirdParty } from "../objects/thirdParty";
import { PurchasesReceiptThirdPartyListCriteria } from "../objects/purchasesReceiptThirdPartyList";
import { initialisePurchaseForecast, initialisePurchaseUnpaid } from "../utils/initialiseForecast";
import { PurchasesReceiptUnpaidListCriteria } from "../objects/purchasesReceiptUnpaidListCriteria";


const initialState: PurchasesReceiptState = {

    forecast_list: [],
    forecast_list_loaded: false,
    selected_forecast: [],
    forecast_refresh_list: false,
    forecast_list_configuration: EMPTY_PURCHASES_RECEIPT_LIST_CONFIGURATION,
    forecast_list_loading: false,
    forecast_list_count: 0,
    forecast_list_criteria: EMPTY_PURCHASES_RECEIPT_LIST_CRITERIA,
    forecast_is_creating: false,
    forecast: null,
    forecast_is_grouping: false,

    provider_list: [],
    provider_list_loaded: false,
    selected_provider: [],
    provider_refresh_list: false,
    provider_list_configuration: EMPTY_PURCHASES_RECEIPT_PROVIDER_LIST_CONFIGURATION,
    provider_list_loading: false,
    provider_list_count: 0,
    provider_list_criteria: EMPTY_PURCHASES_RECEIPT_PROVIDER_LIST_CRITERIA,

    unpaid_list: [],
    unpaid_list_loaded: false,
    selected_unpaid: [],
    unpaid_refresh_list: false,
    unpaid_list_configuration: EMPTY_PURCHASES_RECEIPT_UNPAID_LIST_CONFIGURATION,
    unpaid_list_loading: false,
    unpaid_list_count: 0,
    unpaid_list_criteria: EMPTY_PURCHASES_RECEIPT_UNPAID_LIST_CRITERIA,
    unpaid_is_creating: false,
    unpaid_partial_is_creating: false,
    unpaid: null,

    paid_list: [],
    paid_list_loaded: false,
    selected_paid: [],
    paid_refresh_list: false,
    paid_list_configuration: EMPTY_PURCHASES_RECEIPT_PAID_LIST_CONFIGURATION,
    paid_list_loading: false,
    paid_list_count: 0,
    paid_list_criteria: EMPTY_PURCHASES_RECEIPT_PAID_LIST_CRITERIA,
    paid_is_creating: false,
    collect_invoice: null,
    paid: null,


    third_party_list: [],
    third_party_list_loaded: false,
    selected_third_party: [],
    third_party_refresh_list: false,
    third_party_list_configuration: EMPTY_THIRD_PARTY_LIST_CONFIGURATION,
    third_party_list_loading: false,
    third_party_list_count: 0,
    third_party_list_criteria: EMPTY_THIRD_PARTY_LIST_CRITERIA,
    third_party_is_creating: false,
    third_party: null,


    purchases_receipt: null,
    company: null,
    purchases_stats: EMPTY_PURCHASES_STATS,

    first_preference_loaded: false,

    currencies: [],
    payment_types: {},
    prefered_payment_type: "RM RGT1",
    payment_types_loaded: false
};

export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');

const setCompanyReducer = (state: PurchasesReceiptState, action: PayloadAction<Organisation | null>): PurchasesReceiptState => ({
    ...state, company: action.payload
});
const updatePurchaseReceiptReducer = (state: PurchasesReceiptState, action: PayloadAction<PurchasesReceipt | null>): PurchasesReceiptState => ({
    ...state, purchases_receipt: action.payload
});


const setPreferenceReducer = (state: PurchasesReceiptState, action: PayloadAction<Preferences[]>): PurchasesReceiptState => {
    const { preferences, columns, order_by, order } = setPurchasesReceiptForecastPreference(action.payload, state.forecast_list_configuration.columns);
    const provider_preference = setPurchasesReceiptProviderPreference(action.payload, state.provider_list_configuration.columns);
    const unpaid_preference = setPurchasesReceiptUnpaidPreference(action.payload, state.unpaid_list_configuration.columns);
    const paid_preference = setPurchasesReceiptPaidPreference(action.payload, state.paid_list_configuration.columns);
    const third_party_preference = setThirdPartyPreference(action.payload, state.third_party_list_configuration.columns);
    return {
        ...state,
        first_preference_loaded: true,
        forecast_list_configuration: { ...state.forecast_list_configuration, preferences, columns },
        forecast_list_criteria: { ...state.forecast_list_criteria, order, order_by },
        provider_list_configuration: { ...state.provider_list_configuration, preferences: provider_preference.preferences, columns: provider_preference.columns },
        provider_list_criteria: { ...state.provider_list_criteria, order: provider_preference.order, order_by: provider_preference.order_by },
        unpaid_list_configuration: { ...state.unpaid_list_configuration, preferences: unpaid_preference.preferences, columns: unpaid_preference.columns },
        unpaid_list_criteria: { ...state.unpaid_list_criteria, order: unpaid_preference.order, order_by: unpaid_preference.order_by },
        paid_list_configuration: { ...state.paid_list_configuration, preferences: paid_preference.preferences, columns: paid_preference.columns },
        paid_list_criteria: { ...state.paid_list_criteria, order: paid_preference.order, order_by: paid_preference.order_by },
        third_party_list_configuration: { ...state.third_party_list_configuration, preferences: third_party_preference.preferences, columns: third_party_preference.columns },
        third_party_list_criteria: { ...state.third_party_list_criteria, order: third_party_preference.order, order_by: third_party_preference.order_by }
    };
};



//  ------------------------------ forecast ----------------------------------- //
const toggleForecastRefreshListReducer = (state: PurchasesReceiptState): PurchasesReceiptState => ({
    ...state, forecast_refresh_list: !state.forecast_refresh_list, forecast_list_loaded: false
});

const setSelectedForecastReducer = (state: PurchasesReceiptState, action: PayloadAction<PurchasesReceipt[]>): PurchasesReceiptState => ({
    ...state, selected_forecast: action.payload
});

const addForecastListConfigurationPreferenceReducer = (state: PurchasesReceiptState, action: PayloadAction<Preferences[]>): PurchasesReceiptState => ({
    ...state, forecast_list_configuration: { ...state.forecast_list_configuration, preferences: [...state.forecast_list_configuration.preferences, ...action.payload] }
});

const setForecastListConfigurationPreferenceReducer = (state: PurchasesReceiptState, action: PayloadAction<Preferences[]>): PurchasesReceiptState => ({
    ...state, forecast_list_configuration: { ...state.forecast_list_configuration, preferences: action.payload }
});

const updateForecastListReducer = (state: PurchasesReceiptState, action: PayloadAction<PurchasesReceipt[]>): PurchasesReceiptState => ({
    ...state, forecast_list: action.payload, forecast_list_loaded: true
});
const updateForecastListCriteriaReducer = (state: PurchasesReceiptState, action: PayloadAction<PurchasesReceiptListCriteria>): PurchasesReceiptState => ({
    ...state, forecast_list_criteria: action.payload
});
const updateForecastListLoadingReducer = (state: PurchasesReceiptState, action: PayloadAction<boolean>): PurchasesReceiptState => ({
    ...state, forecast_list_loading: action.payload
});
const updateForecastListCountReducer = (state: PurchasesReceiptState, action: PayloadAction<number>): PurchasesReceiptState => ({
    ...state, forecast_list_count: action.payload
});
const updateForecastIsCreatingReducer = (state: PurchasesReceiptState, action: PayloadAction<boolean>): PurchasesReceiptState => ({
    ...state, forecast_is_creating: action.payload
});
const setColumnsReducer = (state: PurchasesReceiptState, action: PayloadAction<Column[]>): PurchasesReceiptState => ({
    ...state, forecast_list_configuration: { ...state.forecast_list_configuration, columns: action.payload }
});

const toggleColumnReducer = (state: PurchasesReceiptState, action: PayloadAction<number>): PurchasesReceiptState => {
    const columns = cloneDeep([...state.forecast_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, forecast_list_configuration: { ...state.forecast_list_configuration, columns } };
};
const setForecastReducer = (state: PurchasesReceiptState, action: PayloadAction<PurchasesReceipt | null>): PurchasesReceiptState => ({
    ...state, forecast: action.payload ? initialisePurchaseForecast(action.payload) : null
});
const updateForecastIsGroupingReducer = (state: PurchasesReceiptState, action: PayloadAction<boolean>): PurchasesReceiptState => ({
    ...state, forecast_is_grouping: action.payload
});


//  ------------------------------ provider ----------------------------------- //
const toggleProviderRefreshListReducer = (state: PurchasesReceiptState): PurchasesReceiptState => ({
    ...state, provider_refresh_list: !state.provider_refresh_list, provider_list_loaded: false
});

const setSelectedProviderReducer = (state: PurchasesReceiptState, action: PayloadAction<CustomProvider[]>): PurchasesReceiptState => ({
    ...state, selected_provider: action.payload
});

const addProviderListConfigurationPreferenceReducer = (state: PurchasesReceiptState, action: PayloadAction<Preferences[]>): PurchasesReceiptState => ({
    ...state, provider_list_configuration: { ...state.provider_list_configuration, preferences: [...state.provider_list_configuration.preferences, ...action.payload] }
});

const setProviderListConfigurationPreferenceReducer = (state: PurchasesReceiptState, action: PayloadAction<Preferences[]>): PurchasesReceiptState => ({
    ...state, provider_list_configuration: { ...state.provider_list_configuration, preferences: action.payload }
});

const updateProviderListReducer = (state: PurchasesReceiptState, action: PayloadAction<CustomProvider[]>): PurchasesReceiptState => ({
    ...state, provider_list: action.payload, provider_list_loaded: true
});
const updateProviderListCriteriaReducer = (state: PurchasesReceiptState, action: PayloadAction<PurchasesReceiptProviderListCriteria>): PurchasesReceiptState => ({
    ...state, provider_list_criteria: action.payload
});

const updateProviderListLoadingReducer = (state: PurchasesReceiptState, action: PayloadAction<boolean>): PurchasesReceiptState => ({
    ...state, provider_list_loading: action.payload
});
const updateProviderListCountReducer = (state: PurchasesReceiptState, action: PayloadAction<number>): PurchasesReceiptState => ({
    ...state, provider_list_count: action.payload
});
const setProviderColumnsReducer = (state: PurchasesReceiptState, action: PayloadAction<Column[]>): PurchasesReceiptState => ({
    ...state, provider_list_configuration: { ...state.provider_list_configuration, columns: action.payload }
});
const toggleColumnProviderReducer = (state: PurchasesReceiptState, action: PayloadAction<number>): PurchasesReceiptState => {
    const columns = cloneDeep([...state.provider_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, provider_list_configuration: { ...state.provider_list_configuration, columns } };
};


//  ------------------------------ unpaid ----------------------------------- //
const toggleUnpaidRefreshListReducer = (state: PurchasesReceiptState): PurchasesReceiptState => ({
    ...state, unpaid_refresh_list: !state.unpaid_refresh_list, unpaid_list_loaded: false
});

const setSelectedUnpaidReducer = (state: PurchasesReceiptState, action: PayloadAction<Bill[]>): PurchasesReceiptState => ({
    ...state, selected_unpaid: action.payload
});

const addUnpaidListConfigurationPreferenceReducer = (state: PurchasesReceiptState, action: PayloadAction<Preferences[]>): PurchasesReceiptState => ({
    ...state, unpaid_list_configuration: { ...state.unpaid_list_configuration, preferences: [...state.unpaid_list_configuration.preferences, ...action.payload] }
});

const setUnpaidListConfigurationPreferenceReducer = (state: PurchasesReceiptState, action: PayloadAction<Preferences[]>): PurchasesReceiptState => ({
    ...state, unpaid_list_configuration: { ...state.unpaid_list_configuration, preferences: action.payload }
});

const updateUnpaidListReducer = (state: PurchasesReceiptState, action: PayloadAction<Bill[]>): PurchasesReceiptState => ({
    ...state, unpaid_list: action.payload, unpaid_list_loaded: true
});
const updateUnpaidListCriteriaReducer = (state: PurchasesReceiptState, action: PayloadAction<PurchasesReceiptUnpaidListCriteria>): PurchasesReceiptState => ({
    ...state, unpaid_list_criteria: action.payload
});

const updateUnpaidListLoadingReducer = (state: PurchasesReceiptState, action: PayloadAction<boolean>): PurchasesReceiptState => ({
    ...state, unpaid_list_loading: action.payload
});
const updateUnpaidListCountReducer = (state: PurchasesReceiptState, action: PayloadAction<number>): PurchasesReceiptState => ({
    ...state, unpaid_list_count: action.payload
});
const updateUnpaidIsCreatingReducer = (state: PurchasesReceiptState, action: PayloadAction<boolean>): PurchasesReceiptState => ({
    ...state, unpaid_is_creating: action.payload
});
const updateUnpaidPartialIsCreatingReducer = (state: PurchasesReceiptState, action: PayloadAction<boolean>): PurchasesReceiptState => ({
    ...state, unpaid_partial_is_creating: action.payload
});
const setUnpaidColumnsReducer = (state: PurchasesReceiptState, action: PayloadAction<Column[]>): PurchasesReceiptState => ({
    ...state, unpaid_list_configuration: { ...state.unpaid_list_configuration, columns: action.payload }
});
const toggleColumnUnpaidReducer = (state: PurchasesReceiptState, action: PayloadAction<number>): PurchasesReceiptState => {
    const columns = cloneDeep([...state.unpaid_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, unpaid_list_configuration: { ...state.unpaid_list_configuration, columns } };
};
const setUnpaidReducer = (state: PurchasesReceiptState, action: PayloadAction<Bill | null>): PurchasesReceiptState => ({
    ...state, unpaid: action.payload ? initialisePurchaseUnpaid(action.payload) : null
});


//  ------------------------------ paid ----------------------------------- //
const togglePaidRefreshListReducer = (state: PurchasesReceiptState): PurchasesReceiptState => ({
    ...state, paid_refresh_list: !state.paid_refresh_list, paid_list_loaded: false
});

const setSelectedPaidReducer = (state: PurchasesReceiptState, action: PayloadAction<BillPayment[]>): PurchasesReceiptState => ({
    ...state, selected_paid: action.payload
});

const addPaidListConfigurationPreferenceReducer = (state: PurchasesReceiptState, action: PayloadAction<Preferences[]>): PurchasesReceiptState => ({
    ...state, paid_list_configuration: { ...state.paid_list_configuration, preferences: [...state.paid_list_configuration.preferences, ...action.payload] }
});

const setPaidListConfigurationPreferenceReducer = (state: PurchasesReceiptState, action: PayloadAction<Preferences[]>): PurchasesReceiptState => ({
    ...state, paid_list_configuration: { ...state.paid_list_configuration, preferences: action.payload }
});

const updatePaidListReducer = (state: PurchasesReceiptState, action: PayloadAction<BillPayment[]>): PurchasesReceiptState => ({
    ...state, paid_list: action.payload, paid_list_loaded: true
});
const updatePaidListCriteriaReducer = (state: PurchasesReceiptState, action: PayloadAction<PurchasesReceiptPaidListCriteria>): PurchasesReceiptState => ({
    ...state, paid_list_criteria: action.payload
});

const updatePaidListLoadingReducer = (state: PurchasesReceiptState, action: PayloadAction<boolean>): PurchasesReceiptState => ({
    ...state, paid_list_loading: action.payload
});
const updatePaidListCountReducer = (state: PurchasesReceiptState, action: PayloadAction<number>): PurchasesReceiptState => ({
    ...state, paid_list_count: action.payload
});
const updatePaidIsCreatingReducer = (state: PurchasesReceiptState, action: PayloadAction<boolean>): PurchasesReceiptState => ({
    ...state, paid_is_creating: action.payload
});
const setPaidColumnsReducer = (state: PurchasesReceiptState, action: PayloadAction<Column[]>): PurchasesReceiptState => ({
    ...state, paid_list_configuration: { ...state.paid_list_configuration, columns: action.payload }
});
const toggleColumnPaidReducer = (state: PurchasesReceiptState, action: PayloadAction<number>): PurchasesReceiptState => {
    const columns = cloneDeep([...state.paid_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, paid_list_configuration: { ...state.paid_list_configuration, columns } };
};
const setCollectInvoiceReducer = (state: PurchasesReceiptState, action: PayloadAction<Bill | PurchasesReceipt | null>): PurchasesReceiptState => ({
    ...state, collect_invoice: action.payload
});
const setPaidReducer = (state: PurchasesReceiptState, action: PayloadAction<BillPayment | null>): PurchasesReceiptState => ({
    ...state, paid: action.payload
});



//  ------------------------------ third_party ----------------------------------- //
const toggleThirdPartyRefreshListReducer = (state: PurchasesReceiptState): PurchasesReceiptState => ({
    ...state, third_party_refresh_list: !state.third_party_refresh_list, third_party_list_loaded: false
});
const setSelectedThirdPartyReducer = (state: PurchasesReceiptState, action: PayloadAction<ThirdParty[]>): PurchasesReceiptState => ({
    ...state, selected_third_party: action.payload
});
const addThirdPartyListConfigurationPreferenceReducer = (state: PurchasesReceiptState, action: PayloadAction<Preferences[]>): PurchasesReceiptState => ({
    ...state, third_party_list_configuration: { ...state.third_party_list_configuration, preferences: [...state.third_party_list_configuration.preferences, ...action.payload] }
});
const setThirdPartyListConfigurationPreferenceReducer = (state: PurchasesReceiptState, action: PayloadAction<Preferences[]>): PurchasesReceiptState => ({
    ...state, third_party_list_configuration: { ...state.third_party_list_configuration, preferences: action.payload }
});
const toggleColumnThirdPartyReducer = (state: PurchasesReceiptState, action: PayloadAction<number>): PurchasesReceiptState => {
    const columns = cloneDeep([...state.third_party_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, third_party_list_configuration: { ...state.third_party_list_configuration, columns } };
};
const updateThirdPartyListReducer = (state: PurchasesReceiptState, action: PayloadAction<ThirdParty[]>): PurchasesReceiptState => ({
    ...state, third_party_list: action.payload, third_party_list_loaded: true
});
const updateThirdPartyListCriteriaReducer = (state: PurchasesReceiptState, action: PayloadAction<PurchasesReceiptThirdPartyListCriteria>): PurchasesReceiptState => ({
    ...state, third_party_list_criteria: action.payload
});
const updateThirdPartyListLoadingReducer = (state: PurchasesReceiptState, action: PayloadAction<boolean>): PurchasesReceiptState => ({
    ...state, third_party_list_loading: action.payload
});
const updateThirdPartyListCountReducer = (state: PurchasesReceiptState, action: PayloadAction<number>): PurchasesReceiptState => ({
    ...state, third_party_list_count: action.payload
});
const updateThirdPartyIsCreatingReducer = (state: PurchasesReceiptState, action: PayloadAction<boolean>): PurchasesReceiptState => ({
    ...state, third_party_is_creating: action.payload
});
const setThirdPartyColumnsReducer = (state: PurchasesReceiptState, action: PayloadAction<Column[]>): PurchasesReceiptState => ({
    ...state, third_party_list_configuration: { ...state.third_party_list_configuration, columns: action.payload }
});
const setThirdPartyReducer = (state: PurchasesReceiptState, action: PayloadAction<ThirdParty | null>): PurchasesReceiptState => ({
    ...state, third_party: action.payload
});


// ----------- utils ----------------- //
const setPaymentTypesReducer = (state: PurchasesReceiptState, action: PayloadAction<Record<string, string>>): PurchasesReceiptState => ({
    ...state, payment_types: action.payload, payment_types_loaded: true
});
const setPreferedPaymentTypeReducer = (state: PurchasesReceiptState, action: PayloadAction<string>): PurchasesReceiptState => ({
    ...state, prefered_payment_type: action.payload
});

// --------------- currencies ----------------------- //
const setCurrenciesReducer = (state: PurchasesReceiptState, action: PayloadAction<Currency[]>): PurchasesReceiptState => ({
    ...state, currencies: action.payload
});


// ----------------- stats --------------------- //
const setPurchasesStatsReducer = (state: PurchasesReceiptState, action: PayloadAction<PurchasesStats>): PurchasesReceiptState => ({
    ...state, purchases_stats: action.payload
});

export const purchasesReceipt = createSlice({
    name: 'purchases_receipt',
    initialState,
    reducers: {
        setCompany: setCompanyReducer,
        updatePurchaseReceipt: updatePurchaseReceiptReducer,

        setSelectedForecast: setSelectedForecastReducer,
        toggleForecastRefreshList: toggleForecastRefreshListReducer,
        addForecastListConfigurationPreference: addForecastListConfigurationPreferenceReducer,
        setForecastListConfigurationPreference: setForecastListConfigurationPreferenceReducer,
        updateForecastList: updateForecastListReducer,
        updateForecastListCriteria: updateForecastListCriteriaReducer,
        updateForecastListLoading: updateForecastListLoadingReducer,
        updateForecastListCount: updateForecastListCountReducer,
        updateForecastIsCreating: updateForecastIsCreatingReducer,
        setColumns: setColumnsReducer,
        toggleColumn: toggleColumnReducer,
        setForecast: setForecastReducer,
        updateForecastIsGrouping: updateForecastIsGroupingReducer,

        setSelectedProvider: setSelectedProviderReducer,
        toggleProviderRefreshList: toggleProviderRefreshListReducer,
        addProviderListConfigurationPreference: addProviderListConfigurationPreferenceReducer,
        setProviderListConfigurationPreference: setProviderListConfigurationPreferenceReducer,
        updateProviderList: updateProviderListReducer,
        updateProviderListCriteria: updateProviderListCriteriaReducer,
        updateProviderListLoading: updateProviderListLoadingReducer,
        updateProviderListCount: updateProviderListCountReducer,
        setProviderColumns: setProviderColumnsReducer,
        toggleColumnProvider: toggleColumnProviderReducer,

        setSelectedUnpaid: setSelectedUnpaidReducer,
        toggleUnpaidRefreshList: toggleUnpaidRefreshListReducer,
        addUnpaidListConfigurationPreference: addUnpaidListConfigurationPreferenceReducer,
        setUnpaidListConfigurationPreference: setUnpaidListConfigurationPreferenceReducer,
        updateUnpaidList: updateUnpaidListReducer,
        updateUnpaidListCriteria: updateUnpaidListCriteriaReducer,
        updateUnpaidListLoading: updateUnpaidListLoadingReducer,
        updateUnpaidListCount: updateUnpaidListCountReducer,
        updateUnpaidIsCreating: updateUnpaidIsCreatingReducer,
        updateUnpaidPartialIsCreating: updateUnpaidPartialIsCreatingReducer,
        setUnpaidColumns: setUnpaidColumnsReducer,
        toggleColumnUnpaid: toggleColumnUnpaidReducer,
        setUnpaid: setUnpaidReducer,

        setSelectedThirdParty: setSelectedThirdPartyReducer,
        toggleThirdPartyRefreshList: toggleThirdPartyRefreshListReducer,
        addThirdPartyListConfigurationPreference: addThirdPartyListConfigurationPreferenceReducer,
        setThirdPartyListConfigurationPreference: setThirdPartyListConfigurationPreferenceReducer,
        updateThirdPartyList: updateThirdPartyListReducer,
        updateThirdPartyListCriteria: updateThirdPartyListCriteriaReducer,
        updateThirdPartyListLoading: updateThirdPartyListLoadingReducer,
        updateThirdPartyListCount: updateThirdPartyListCountReducer,
        updateThirdPartyIsCreating: updateThirdPartyIsCreatingReducer,
        toggleColumnThirdParty: toggleColumnThirdPartyReducer,
        setThirdPartyColumns: setThirdPartyColumnsReducer,
        setThirdParty: setThirdPartyReducer,

        setSelectedPaid: setSelectedPaidReducer,
        togglePaidRefreshList: togglePaidRefreshListReducer,
        addPaidListConfigurationPreference: addPaidListConfigurationPreferenceReducer,
        setPaidListConfigurationPreference: setPaidListConfigurationPreferenceReducer,
        updatePaidList: updatePaidListReducer,
        updatePaidListCriteria: updatePaidListCriteriaReducer,
        updatePaidListLoading: updatePaidListLoadingReducer,
        updatePaidListCount: updatePaidListCountReducer,
        updatePaidIsCreating: updatePaidIsCreatingReducer,
        setPaidColumns: setPaidColumnsReducer,
        toggleColumnPaid: toggleColumnPaidReducer,
        setCollectInvoice: setCollectInvoiceReducer,
        setPaid: setPaidReducer,

        setPurchasesStats: setPurchasesStatsReducer,
        setPaymentTypes: setPaymentTypesReducer,
        setPreferedPaymentType: setPreferedPaymentTypeReducer,

        setCurrencies: setCurrenciesReducer,

        resizeForecastColumn(state, action: PayloadAction<ColumnResizePayload>) {
            state.forecast_list_configuration.columns = applyColumnResize(state.forecast_list_configuration.columns, action.payload);
        },
        resizeProviderColumn(state, action: PayloadAction<ColumnResizePayload>) {
            state.provider_list_configuration.columns = applyColumnResize(state.provider_list_configuration.columns, action.payload);
        },
        resizeUnpaidColumn(state, action: PayloadAction<ColumnResizePayload>) {
            state.unpaid_list_configuration.columns = applyColumnResize(state.unpaid_list_configuration.columns, action.payload);
        },
        resizePaidColumn(state, action: PayloadAction<ColumnResizePayload>) {
            state.paid_list_configuration.columns = applyColumnResize(state.paid_list_configuration.columns, action.payload);
        },
        resizeThirdPartyColumn(state, action: PayloadAction<ColumnResizePayload>) {
            state.third_party_list_configuration.columns = applyColumnResize(state.third_party_list_configuration.columns, action.payload);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, setPreferenceReducer);
    }
});

export const {
    setCompany,
    setSelectedForecast,
    toggleForecastRefreshList,
    addForecastListConfigurationPreference,
    setForecastListConfigurationPreference,
    updateForecastList,
    updateForecastListCriteria,
    updatePurchaseReceipt,
    updateForecastListLoading,
    updateForecastListCount,
    updateForecastIsCreating,
    setColumns,
    toggleColumn,
    setForecast,
    updateForecastIsGrouping,

    setSelectedProvider,
    toggleProviderRefreshList,
    addProviderListConfigurationPreference,
    setProviderListConfigurationPreference,
    updateProviderList,
    updateProviderListCriteria,
    updateProviderListLoading,
    updateProviderListCount,
    setProviderColumns,
    toggleColumnProvider,

    setSelectedUnpaid,
    toggleUnpaidRefreshList,
    addUnpaidListConfigurationPreference,
    setUnpaidListConfigurationPreference,
    updateUnpaidList,
    updateUnpaidListCriteria,
    updateUnpaidListLoading,
    updateUnpaidListCount,
    updateUnpaidIsCreating,
    updateUnpaidPartialIsCreating,
    setUnpaidColumns,
    toggleColumnUnpaid,
    setUnpaid,

    setSelectedPaid,
    togglePaidRefreshList,
    addPaidListConfigurationPreference,
    setPaidListConfigurationPreference,
    updatePaidList,
    updatePaidListCriteria,
    updatePaidListLoading,
    updatePaidListCount,
    updatePaidIsCreating,
    setPaidColumns,
    toggleColumnPaid,
    setCollectInvoice,
    setPaid,

    setSelectedThirdParty,
    toggleThirdPartyRefreshList,
    addThirdPartyListConfigurationPreference,
    setThirdPartyListConfigurationPreference,
    toggleColumnThirdParty,
    updateThirdPartyList,
    updateThirdPartyListCriteria,
    updateThirdPartyListLoading,
    updateThirdPartyListCount,
    updateThirdPartyIsCreating,
    setThirdPartyColumns,
    setThirdParty,

    setPaymentTypes,
    setPreferedPaymentType,
    setPurchasesStats,

    setCurrencies,

    resizeForecastColumn,
    resizeProviderColumn,
    resizeUnpaidColumn,
    resizePaidColumn,
    resizeThirdPartyColumn,
} = purchasesReceipt.actions;